import { gql, useQuery, useMutation } from '@apollo/client'
import {
  ChevronLeft,
  Delete,
  Edit,
  Download,
  MoreVert,
} from '@mui/icons-material'
import {
  Box,
  Stack,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Button,
  TextField as MuiTextField,
} from '@mui/material'
import {
  useGridApiRef,
  GridCellParams,
  DataGrid,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridActionsCellItem,
  GridRowId,
  GridRenderEditCellParams,
  GridToolbar,
} from '@mui/x-data-grid'
import { Query } from '@promoboxx/graphql-gateway-types'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { Breadcrumb, Breadcrumbs } from '@src/components/shared/Breadcrumbs'
import ConfirmDialog from '@src/lib/ConfirmDialog'
import { currentRetailerAccountIdSelector } from '@src/stores/app/selectors'

import leadsPaths from './leadPaths'
import { GET_LEADS } from './Leads'

const GET_LEAD_IMPORT = gql`
  query GetLeadImport($getLeadImportId: ID!, $fields: [String!]) {
    getLeadImport(id: $getLeadImportId, fields: $fields) {
      id
      brand_id
      retailer_id
      name
      description
      created_at
      updated_at
      manual_input
      completed_at
      method_choice
      import_file_name
      import_content_type
      import_file_size
      import_updated_at
      created_by_id
      updated_by_id
      deleted_at
      error_at
    }
    getLeadsList(resource_type: "lead_import", resource_id: $getLeadImportId) {
      id
      custom_data {
        name
        value
      }
      campaign_id
      created_at
      updated_at
      retailer_id
      brand_id
      deleted_at
      lead_import_id
      created_by_id
      updated_by_id
      email_address
      first_name
      last_name
      event_id
      consumer_id
      disposition
      share_setting_id
      state
    }
  }
`

const UPDATE_LEAD_IMPORT = gql`
  mutation UpdateLeadImport($id: ID!, $leadImport: UpdateLeadImportInput) {
    updateLeadImport(id: $id, lead_import: $leadImport) {
      id
      name
    }
  }
`

const UPDATE_LEAD = gql`
  mutation UpdateLead($id: ID!, $lead: UpdateLeadInput) {
    updateLead(id: $id, lead: $lead) {
      id
      disposition
    }
  }
`
const DELETE_LEAD = gql`
  mutation DeleteLead($id: ID!) {
    deleteLead(id: $id)
  }
`
const DELETE_LEAD_IMPORT = gql`
  mutation DeleteLeadImport($id: ID!) {
    deleteLeadImport(id: $id)
  }
`
const convertToCSV = (tableData: any[]) => {
  if (!tableData.length) {
    return
  }
  const header = `${Object.keys(tableData[0]).join(',')}\n`
  const rows = tableData
    .map((row) => {
      return Object.values(row)
        .map((value) => {
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"') || value.includes('\n'))
          ) {
            return `"${value.replace(/"/g, '""')}"`
          } else {
            return value
          }
        })
        .join(',')
    })
    .join('\n')

  const csv = header + rows
  return csv
}

const leadStatusOptions = [
  { label: I18n.t('leads.leadStatuses.none'), value: 'null' },
  {
    label: I18n.t('leads.leadStatuses.contactAttempted'),
    value: 'contact_attempted',
  },
  { label: I18n.t('leads.leadStatuses.contacted'), value: 'contacted' },
  { label: I18n.t('leads.leadStatuses.qualified'), value: 'qualified' },
  { label: I18n.t('leads.leadStatuses.converted'), value: 'converted' },
  { label: I18n.t('leads.leadStatuses.disqualified'), value: 'disqualified' },
]

const emailStatusOptions = [
  { label: I18n.t('leads.emailStatuses.subscribed'), value: 'subscribed' },
  { label: I18n.t('leads.emailStatuses.unsubscribed'), value: 'unsubscribed' },
]

const LeadImport: React.FC<React.PropsWithChildren<{ match: any }>> = (
  props,
) => {
  const { importId, retailerAccountSlug } = props.match.params
  const retailerAccountId = useSelector(currentRetailerAccountIdSelector)
  const gridApiRef = useGridApiRef()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [isEditing, setIsEditing] = useState(false)
  const [isDelDialogOpen, setIsDelDialogOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [updateLeadImport] = useMutation(UPDATE_LEAD_IMPORT)
  const [deleteLeadImport] = useMutation(DELETE_LEAD_IMPORT)
  const [updateLead] = useMutation(UPDATE_LEAD)
  const [deleteLead] = useMutation(DELETE_LEAD)
  const [deleteCb, setDeleteCb] = useState<() => void>(() => () => {})

  const { data, loading, refetch } = useQuery<Query>(GET_LEAD_IMPORT, {
    variables: {
      getLeadImportId: importId,
    },
  })
  const { refetch: refreshOnDelete } = useQuery<Query>(GET_LEADS, {
    variables: {
      resourceId: retailerAccountId,
      resourceType: 'retailer_accounts',
      fields: ['leads_count', 'leads_unsubscribed_count'],
      params: {
        filters: {
          by_state: 'unsubscribed',
        },
      },
    },
    skip: true,
  })

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const leads = useMemo(
    () =>
      data?.getLeadsList?.map((l) => {
        return {
          id: l?.id || '',
          email_address: l?.email_address || '',
          custom_data: l?.custom_data || [],
          disposition: l?.disposition || '',
          state: l?.state || '',
        }
      }) || [],
    [data?.getLeadsList],
  )

  const handleDownloadLeadImport = () => {
    try {
      const tableData = leads.map(({ email_address, disposition, state }) => ({
        email_address,
        disposition,
        state,
      }))
      const csv = convertToCSV(tableData)
      if (!csv) {
        throw new Error('No data to download')
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.visibility = 'hidden'
      link.setAttribute('href', url)
      link.setAttribute('download', data?.getLeadImport?.name || 'leads.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (e) {
      console.error(e)
      enqueueSnackbar(I18n.t('leads.leadImportDownloadFailed'), {
        variant: 'error',
      })
    }
  }

  const handleDeleteLeadImport = () => {
    setIsDelDialogOpen(true)
    setDeleteCb(() => () => {
      deleteLeadImport({
        variables: {
          id: importId,
        },
      })
        .then((resp) => {
          if (resp.data) {
            enqueueSnackbar(I18n.t('leads.leadImportDeleted'), {
              variant: 'success',
            })
            history.push(
              `${leadsPaths.index.build({ retailerAccountSlug })}?refetch`,
            )
            refreshOnDelete()
          } else {
            enqueueSnackbar(I18n.t('leads.leadImportDeleteFailed'), {
              variant: 'error',
            })
          }
        })
        .catch((e) => {
          console.error(e)
          enqueueSnackbar(I18n.t('leads.leadImportDeleteFailed'), {
            variant: 'error',
          })
        })
        .finally(() => setIsDelDialogOpen(false))
    })
  }

  const handleUpdateLead = useCallback(
    async (params: GridCellParams, disposition: String | null) => {
      const resp = await updateLead({
        variables: {
          id: params.row.id,
          lead: {
            disposition: disposition === 'null' ? null : disposition,
          },
        },
      })
      if (resp?.data) {
        enqueueSnackbar(I18n.t('leads.leadUpdated'), { variant: 'success' })
        gridApiRef.current.setEditCellValue(
          { id: params.id, field: params.field, value: disposition },
          params.field,
        )
      } else {
        enqueueSnackbar(I18n.t('leads.leadUpdateFailed'), {
          variant: 'error',
        })
      }
      gridApiRef.current.stopCellEditMode({
        id: params.id,
        field: params.field,
      })
    },
    [enqueueSnackbar, updateLead, gridApiRef],
  )

  const handleDeleteLead = useCallback(
    (id: GridRowId) => {
      setIsDelDialogOpen(true)
      setDeleteCb(() => {
        return () => {
          deleteLead({ variables: { id } })
            .then((resp) => {
              resp?.data
                ? enqueueSnackbar(I18n.t('leads.leadDeleted'), {
                    variant: 'success',
                  })
                : enqueueSnackbar(I18n.t('leads.leadDeletedFailed'), {
                    variant: 'error',
                  })
              refetch()
            })
            .catch((e) => {
              console.error(e)
              enqueueSnackbar(I18n.t('leads.leadDeletedFailed'), {
                variant: 'error',
              })
            })
            .finally(() => setIsDelDialogOpen(false))
        }
      })
    },
    [deleteLead, enqueueSnackbar, refetch],
  )

  const leadImportColumn = useMemo(
    () => [
      {
        field: 'email_address',
        headerName: I18n.t('shared.emailAddress'),
        flex: 0.5,
      },
      {
        field: 'name',
        headerName: I18n.t('shared.name'),
        flex: 0.3,
        valueGetter: (params: GridValueGetterParams) =>
          `${
            params.row.custom_data.find(
              (el: { name: String; value: String }) => el?.name === 'name',
            )?.value || ''
          }`,
        filterable: false,
        sortable: false,
      },
      {
        field: 'disposition',
        headerName: I18n.t('leads.leadStatus'),
        flex: 0.3,
        type: 'singleSelect',
        valueOptions: leadStatusOptions,
        editable: true,
        valueFormatter: (params: GridValueFormatterParams) =>
          leadStatusOptions.find((opt) => opt.value === String(params.value))
            ?.label || I18n.t('leads.leadStatuses.none'),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <MuiTextField
            select
            id={`lead-status-${params.row.id}-select-id`}
            value={params.value || 'null'}
            onChange={(e) => handleUpdateLead(params, e.target.value)}
            fullWidth
            variant="standard"
          >
            {leadStatusOptions.map((option) => (
              <MenuItem
                key={`${params.row.id}-${option.value}`}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </MuiTextField>
        ),
      },
      {
        field: 'state',
        headerName: I18n.t('leads.emailStatus'),
        flex: 0.3,
        type: 'singleSelect',
        valueOptions: emailStatusOptions,
        valueFormatter: (params: GridValueFormatterParams) =>
          emailStatusOptions.find((opt) => opt.value === params.value)?.label ||
          '',
      },

      {
        field: 'action',
        type: 'actions',
        headerName: I18n.t('shared.actions'),
        flex: 0.3,
        getActions: (params: GridValueGetterParams) => [
          <GridActionsCellItem
            icon={<Delete />}
            label={I18n.t('shared.delete')}
            onClick={() => handleDeleteLead(params.row.id)}
          />,
        ],
      },
    ],
    [handleDeleteLead, handleUpdateLead],
  )

  return (
    <Box sx={{ padding: 4 }}>
      <Breadcrumbs marginY={2}>
        <Breadcrumb
          Icon={ChevronLeft}
          to={leadsPaths.index.build({ retailerAccountSlug })}
        >
          {I18n.t('leads.backToLists')}
        </Breadcrumb>
      </Breadcrumbs>
      {!loading && (
        <Box>
          <Paper>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ padding: 4 }}
            >
              {isEditing ? (
                <Formik
                  initialValues={{
                    name: data?.getLeadImport?.name,
                  }}
                  onSubmit={async (values) => {
                    const { data: updated } = await updateLeadImport({
                      variables: {
                        id: importId,
                        leadImport: {
                          name: values.name,
                        },
                      },
                    })
                    updated?.updateLeadImport?.id
                      ? enqueueSnackbar(I18n.t('leads.leadImportUpdated'), {
                          variant: 'success',
                        })
                      : enqueueSnackbar(
                          I18n.t('leads.leadImportUpdateFailed'),
                          { variant: 'success' },
                        )
                    setIsEditing(false)
                  }}
                >
                  {({ isSubmitting, resetForm }) => (
                    <Form style={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        alignContent="center"
                        justifyContent="space-between"
                      >
                        <Field
                          name="name"
                          type="text"
                          component={TextField}
                          variant="standard"
                        />
                        <Stack
                          direction="row"
                          alignContent="center"
                          spacing={2}
                          marginY="auto"
                        >
                          <Button
                            color="primary"
                            variant="contained"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {I18n.t('shared.save')}
                          </Button>
                          <Button
                            onClick={() => {
                              setIsEditing(false)
                              resetForm()
                            }}
                          >
                            {I18n.t('shared.cancel')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              ) : (
                <Typography marginY={1} variant="h6">
                  {data?.getLeadImport?.name}
                </Typography>
              )}
              <Stack>
                <IconButton
                  aria-label="more"
                  aria-controls="table-actions-menu"
                  aria-haspopup="true"
                  onClick={handleClick}
                  disabled={isEditing}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  id="table-actions-menu"
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorEl={anchorEl}
                  MenuListProps={{
                    'aria-labelledby': 'table-actions-menu',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                >
                  <MenuItem
                    color="inherit"
                    onClick={() => {
                      setIsEditing(true)
                      setAnchorEl(null)
                    }}
                  >
                    <ListItemIcon>
                      <Edit />
                    </ListItemIcon>
                    <ListItemText>
                      {I18n.t('leads.leadsListActions.edit')}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem color="inherit" onClick={handleDownloadLeadImport}>
                    <ListItemIcon>
                      <Download />
                    </ListItemIcon>
                    <ListItemText>
                      {I18n.t('leads.leadsListActions.download')}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem color="inherit" onClick={handleDeleteLeadImport}>
                    <ListItemIcon>
                      <Delete />
                    </ListItemIcon>
                    <ListItemText>
                      {I18n.t('leads.leadsListActions.delete')}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </Stack>
            </Stack>
            <Box sx={{ height: '70vh', flexGrow: 1 }}>
              <DataGrid
                slots={{ toolbar: GridToolbar }}
                apiRef={gridApiRef}
                loading={loading}
                rows={leads}
                columns={leadImportColumn}
                editMode="cell"
              />
            </Box>
          </Paper>
        </Box>
      )}
      <ConfirmDialog
        open={isDelDialogOpen}
        title={I18n.t('shared.confirmDeletion')}
        content={I18n.t('shared.areYouSure')}
        onConfirmClick={deleteCb}
        onClose={() => {
          setIsDelDialogOpen(false)
          setDeleteCb(function () {})
        }}
      />
    </Box>
  )
}

export default LeadImport
